
import { useMemo } from 'react';
import styles from './index.module.scss'
import { IMAGE_TYPE, RESOURCE_TYPE } from '@utils/CONST'
import API from '@api/api'
import { useNavigate } from 'react-router-dom';
import { workflowOfSendTask } from '@utils/workflowOfBuildTaskV2';
import { Popover, Divider } from 'antd';
import Iconfont from '@utils/Iconfont';

const BottomBtnGroups = (props) => {
    const { showTopBtns, imageType, item } = props
    const navigate = useNavigate()

    // 辅助组件：渲染单个菜单项
    const MenuLink = ({ icon, label, onClick }) => (
        <p className={styles.footer_text} onClick={onClick}>
            <Iconfont type={icon} className={styles.Icon} />
            {label}
        </p>
    )

    const handleClick = async (e, jumpTotype, targetType = "inputImage") => {
        e.stopPropagation()
        let noWaterRes = await API.commonDownload({ taskId: item?.taskId })
        if (noWaterRes?.code != 0) throw new Error(noWaterRes?.message)
        const image = noWaterRes.data

        // 跳转AI编辑
        const aiEditModes = ['handRepair', 'magicErase', 'image_extraction', 'imageResize']
        if (aiEditModes.includes(targetType)) {
            if (targetType === 'image_extraction' || targetType === 'imageResize') {
                window.open(image ? `/home/${targetType}?url=${encodeURIComponent(image)}` : `/home/${targetType}`, '_blank')
            } else {
                navigate("/home/aiEdit", { state: { menuKey: targetType, picUrl: image } })
            }
            return
        }

        const res = await workflowOfSendTask(jumpTotype, image, targetType)
        if (!res) return
        const { pathname, options } = res
        navigate(pathname, options)
    }

    const isPersonScene = useMemo(() => {
        const commodityTypes = [
            IMAGE_TYPE.COMMODITY,
            IMAGE_TYPE.COMMODITYRECOLOR,
            IMAGE_TYPE.WATERMARK_REMOVE
        ]
        if (commodityTypes.includes(imageType)) return false
        if (imageType === IMAGE_TYPE.CLOTH_RECOLOR && !item?.clothReColorIsPerson) return false
        return true
    }, [imageType, item])

    const leftMenus = useMemo(() => {
        if (!isPersonScene) return []

        const list = [
            { label: '换衣服', icon: 'icon-aiModelOutlined', type: IMAGE_TYPE.CLOTHES, resourceType: RESOURCE_TYPE.TRYON_MODEL },
            { label: '换模特', icon: 'icon-aiModelOutlined', type: IMAGE_TYPE.MODEL },
            { label: '换背景', icon: 'icon-aiModelOutlined', type: IMAGE_TYPE.BACKGROUND, condition: imageType !== IMAGE_TYPE.MODEL }, // 换模特时不显示换背景
            { label: '换鞋子', icon: 'icon-shoesOutlined', type: IMAGE_TYPE.SHOES, resourceType: RESOURCE_TYPE.TRYON_SHOES },
            { label: '换眼镜', icon: 'icon-glassesOutlined', type: IMAGE_TYPE.GLASSES_TRYON, resourceType: RESOURCE_TYPE.GLASSES_TRYON },
            { label: '换姿势', icon: 'icon-poseFantasyOutlined', type: IMAGE_TYPE.POSE_TRYON },
            { label: '服饰换色', icon: 'icon-recolorOutlined', type: IMAGE_TYPE.CLOTH_RECOLOR },
            { label: '同版型试衣', icon: 'icon-sametryOutlined', type: IMAGE_TYPE.SIM_TRYON, resourceType: RESOURCE_TYPE.SIM_TRYON },
        ]

        // 过滤掉当前页面类型 (去重)
        return list.filter(m => m.type !== imageType && (m.condition === undefined || m.condition))
    }, [isPersonScene, imageType])

    // 配置中间菜单（混合功能）
    const middleMenus = useMemo(() => {
        const list = []

        // 2. 通用功能 (除了当前类型外都显示)
        if (imageType !== IMAGE_TYPE.WATERMARK_REMOVE) {
            list.push({ label: '去水印', icon: 'icon-removeWatermark', type: IMAGE_TYPE.WATERMARK_REMOVE })
        }


        // 1. 人像场景补充功能
        if (isPersonScene) {
            const personExtras = [
                { label: '换搭配', icon: 'icon-outfitSmallOutlined', type: IMAGE_TYPE.FUSION_WEAR, resourceType: RESOURCE_TYPE.FUSIONWEAR_MODEL },
                { label: '去种草', icon: 'icon-sProductshowcaseOutlined', type: IMAGE_TYPE.GRASS_PLANT },
                { label: '图生视频', icon: 'icon-tryonVideoOutlined', type: IMAGE_TYPE.IMAGE_TO_VIDEO },
                { label: '动作模仿', icon: 'icon-tryonVideoOutlined', type: IMAGE_TYPE.MODEL_VIDEO },
            ]
            list.push(...personExtras.filter(m => m.type !== imageType))
        }


        if (imageType !== IMAGE_TYPE.COMMODITYRECOLOR) {
            list.push({ label: '商品换色', icon: 'icon-recolorOutlined', type: IMAGE_TYPE.COMMODITYRECOLOR })
        }

        // 3. 商品/去水印场景的特定关联功能
        // 商品换背：仅在商品换色或去水印时显示
        if ([IMAGE_TYPE.COMMODITYRECOLOR, IMAGE_TYPE.WATERMARK_REMOVE].includes(imageType)) {
            list.push({ label: '商品换背', icon: 'icon-commidityOutlined', type: IMAGE_TYPE.COMMODITY })
        }

        // 商品/商品换色/去水印：显示 图生视频 和 服饰换色
        // 注意：人像场景已经在上面加了图生视频，这里主要覆盖非人像场景
        const commodityRelatedTypes = [IMAGE_TYPE.COMMODITY, IMAGE_TYPE.COMMODITYRECOLOR, IMAGE_TYPE.WATERMARK_REMOVE]
        if (commodityRelatedTypes.includes(imageType)) {
            // 简单处理去重：如果列表里还没有才添加
            if (!list.find(m => m.type === IMAGE_TYPE.IMAGE_TO_VIDEO) && imageType !== IMAGE_TYPE.IMAGE_TO_VIDEO) {
                list.push({ label: '图生视频', icon: 'icon-tryonVideoOutlined', type: IMAGE_TYPE.IMAGE_TO_VIDEO })
            }
            if (!list.find(m => m.type === IMAGE_TYPE.CLOTH_RECOLOR) && imageType !== IMAGE_TYPE.CLOTH_RECOLOR) {
                list.push({ label: '服饰换色', icon: 'icon-recolorOutlined', type: IMAGE_TYPE.CLOTH_RECOLOR })
            }
        }

        return list
    }, [isPersonScene, imageType])

    // 配置右侧菜单（工具类）
    const rightMenus = useMemo(() => {
        const list = []
        // 手部修复：非商品类显示
        if (![IMAGE_TYPE.COMMODITY, IMAGE_TYPE.COMMODITYRECOLOR].includes(imageType)) {
            list.push({ label: '修手', icon: 'icon-handRepairOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'handRepair' })
        }

        list.push(
            { label: '抠图', icon: 'icon-mattingOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'image_extraction' },
            { label: '改尺寸', icon: 'icon-picResizeOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'imageResize' },
            { label: '去杂物', icon: 'icon-magicRemoveOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'magicErase' },
            { label: '修光影', icon: 'icon-lightIconOutlined', type: IMAGE_TYPE.AIEDIT, targetType: 'filllight' },
        )
        return list
    }, [imageType])


    const popover = (
        <div className={styles.popoverContent}>
            {/* 左侧人像功能 */}
            {leftMenus.length > 0 && (
                <>
                    <div className={styles.popoverContentLeft}>
                        {leftMenus.map((menu, i) => (
                            <MenuLink
                                key={i}
                                icon={menu.icon}
                                label={menu.label}
                                onClick={(e) => handleClick(e, menu.type, menu.resourceType)}
                            />
                        ))}
                    </div>
                    <Divider type="vertical" style={{ height: '150px', marginRight: '20px' }} />
                </>
            )}

            {/* 中间混合功能 */}
            <div className={styles.popoverContentLeft}>
                {middleMenus.map((menu, i) => (
                    <MenuLink
                        key={i}
                        icon={menu.icon}
                        label={menu.label}
                        onClick={(e) => handleClick(e, menu.type, menu.resourceType)}
                    />
                ))}
            </div>

            <Divider type="vertical" style={{ height: '150px', marginRight: '20px' }} />

            {/* 右侧工具功能 */}
            <div className={styles.popoverContentRight}>
                {rightMenus.map((menu, i) => (
                    <MenuLink
                        key={i}
                        icon={menu.icon}
                        label={menu.label}
                        onClick={(e) => handleClick(e, menu.type, menu.targetType)}
                    />
                ))}
            </div>
        </div>
    )

    const MoreOutMenuContent = (
        <div className={styles.MoreOutMenuContent}>
            <Popover placement="top" content={popover}>
                <span className={`${styles.spanItem} ${styles.spanItem2}`}><Iconfont type="icon-menuOutlined" /></span>
            </Popover>
        </div>
    )

    return (
        (showTopBtns && imageType != IMAGE_TYPE.MODEL_VIDEO && imageType != IMAGE_TYPE.IMAGE_TO_VIDEO) &&
        (<div className={styles.BottomBtnGroups}>
            {MoreOutMenuContent}
        </div>)
    )
}

export default BottomBtnGroups